import type { ReactNode } from 'react';
import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Hint } from '@/components/Hint';

/**
 * `mark` sits to the left of the whole title block — the vendor logo on a
 * journey. `description` is content (a journey's label); `about` is the
 * explanation of what the page holds, kept behind an info icon so no page
 * opens with a paragraph.
 */
export function PageHeader({ eyebrow, title, description, about, actions, mark }: {
  eyebrow?: string;
  title: string;
  description?: string;
  about?: string;
  actions?: ReactNode;
  mark?: ReactNode;
}) {
  return (
    <header className="flex shrink-0 flex-wrap items-center justify-between gap-5 px-8 pb-3.5 pt-[22px] max-md:gap-4 max-md:px-4 max-md:pb-3 max-md:pt-4">
      {mark && <div className="shrink-0">{mark}</div>}
      <div className={cn('min-w-0', mark && 'flex-[1_1_20rem]')}>
        {eyebrow && <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[.1em] text-muted-foreground">{eyebrow}</div>}
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-[30px] font-extrabold leading-[1.1] tracking-[-.03em] wrap-anywhere max-md:text-[24px]">{title}</h1>
          {about && (
            <Hint content={about} side="top" align="start" tap contentClassName="w-80 max-w-[calc(100vw-2rem)] text-sm">
              <button
                type="button"
                aria-label={`About ${title}`}
                className="inline-flex text-muted-foreground hover:text-foreground pointer-coarse:-m-3.5 pointer-coarse:p-3.5"
              >
                <Info className="h-4 w-4" />
              </button>
            </Hint>
          )}
        </div>
        {description && <p className="mt-2 max-w-[72ch] text-[14px] text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2.5">{actions}</div>}
    </header>
  );
}
